import {
  COLORS,
  drawArc,
  drawArrow,
  drawBall,
  drawDashedLine,
  drawPill,
  drawText,
  drawTrail,
  hexToRgba,
} from './shared.js';

function geometry(width, height) {
  const radius = Math.max(118, Math.min(width * 0.31, height * 0.34, 340));
  return {
    center: { x: width * 0.5, y: height * 0.48 },
    radius,
  };
}

function ballPoint(frame) {
  const { width, height, state } = frame;
  const { center, radius } = geometry(width, height);
  return {
    x: center.x + Math.sin(state.theta) * radius,
    y: center.y + Math.cos(state.theta) * radius,
    center,
    radius,
  };
}

export const circularRenderer = {
  trailPoint(frame) {
    const point = ballPoint(frame);
    return { x: point.x, y: point.y };
  },

  draw(ctx, frame) {
    const { width, height, state, params, metrics, options, accent } = frame;
    const { center, radius } = geometry(width, height);
    const ball = ballPoint(frame);
    const ballRadius = Math.min(22, Math.max(9, 8 + params.mass * 3.4));
    const theta = state.theta;

    // 圆轨道
    ctx.save();
    const orbitGradient = ctx.createLinearGradient(center.x - radius, center.y - radius, center.x + radius, center.y + radius);
    orbitGradient.addColorStop(0, hexToRgba(accent, 0.1));
    orbitGradient.addColorStop(0.5, 'rgba(148,190,220,.2)');
    orbitGradient.addColorStop(1, hexToRgba(accent, 0.55));
    ctx.strokeStyle = orbitGradient;
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(178, 208, 232, 0.12)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(center.x, center.y, radius - 7, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();

    // 方向刻度：物理角 θ 从最低点逆时针量起
    for (let i = 0; i < 24; i += 1) {
      const angle = (i / 24) * Math.PI * 2;
      const inner = radius - (i % 6 === 0 ? 14 : 8);
      if (i % 6 === 0) {
        const physical = ((90 - i * 15) % 360 + 360) % 360;
        drawText(
          ctx,
          `${physical}°`,
          center.x + Math.cos(angle) * (inner - 2),
          center.y + Math.sin(angle) * (inner - 2),
          { color: 'rgba(170,202,224,.36)', font: '500 9px "Cascadia Code", monospace', align: 'center' },
        );
      }
    }

    // 最高点标记
    drawDashedLine(ctx, { x: center.x - radius - 22, y: center.y - radius }, { x: center.x + radius + 22, y: center.y - radius }, {
      color: 'rgba(244,114,182,.28)',
      dash: [4, 6],
    });
    drawText(ctx, '最高点', center.x + radius + 30, center.y - radius, {
      color: 'rgba(244,114,182,.62)',
      font: '600 10px "Microsoft YaHei", sans-serif',
      align: 'left',
    });
    drawText(ctx, '最低点', center.x + 34, center.y + radius + 18, {
      color: 'rgba(244,114,182,.45)',
      font: '600 10px "Microsoft YaHei", sans-serif',
      align: 'left',
    });

    // 圆心与角度
    ctx.save();
    ctx.fillStyle = 'rgba(205, 229, 244, .45)';
    ctx.beginPath();
    ctx.arc(center.x, center.y, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    drawArc(ctx, center, 44, Math.PI / 2, Math.PI / 2 - theta, {
      color: hexToRgba(accent, 0.55),
      width: 1.4,
      anticlockwise: theta > 0,
    });
    drawText(ctx, `θ ${(((theta * 180) / Math.PI) % 360).toFixed(0)}°`, center.x + 58, center.y + 24, {
      color: hexToRgba(accent, 0.75),
      font: '600 10px "Cascadia Code", monospace',
      align: 'center',
    });

    // 杆
    ctx.save();
    ctx.strokeStyle = 'rgba(233, 244, 255, 0.72)';
    ctx.lineWidth = 2;
    ctx.shadowColor = hexToRgba(accent, 0.35);
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.moveTo(center.x, center.y);
    ctx.lineTo(ball.x, ball.y);
    ctx.stroke();
    ctx.restore();

    if (frame.trail?.length > 1) drawTrail(ctx, frame.trail, accent, { width: 3, alpha: 0.55 });

    // 小球
    drawBall(ctx, ball.x, ball.y, ballRadius, accent);

    // 速度
    if (options.vectors) {
      const speed = Math.abs(state.omega) * params.radius;
      if (speed > 0.02) {
        const dir = Math.sign(state.omega);
        const tangent = { x: Math.cos(theta) * dir, y: -Math.sin(theta) * dir };
        const length = Math.min(88, Math.max(20, speed * 12));
        drawArrow(
          ctx,
          { x: ball.x + tangent.x * (ballRadius + 3), y: ball.y + tangent.y * (ballRadius + 3) },
          {
            x: ball.x + tangent.x * (ballRadius + 3 + length),
            y: ball.y + tangent.y * (ballRadius + 3 + length),
          },
          {
            color: COLORS.cyan,
            width: 2,
            head: 7,
            label: `v ${speed.toFixed(2)}`,
            labelOffset: { x: tangent.x * 18, y: tangent.y * 18 - 6 },
            glow: true,
          },
        );
      }

      // 重力
      drawArrow(
        ctx,
        { x: ball.x, y: ball.y + ballRadius * 0.4 },
        { x: ball.x, y: ball.y + ballRadius * 0.4 + 46 },
        { color: COLORS.pink, width: 1.5, head: 6, label: 'mg', labelOffset: { x: 16, y: 6 } },
      );

      // 向心加速度
      const inward = { x: (center.x - ball.x) / radius, y: (center.y - ball.y) / radius };
      const anLength = Math.min(82, Math.max(18, metrics.extras.centripetalAcceleration * 0.85));
      drawArrow(
        ctx,
        { x: ball.x + inward.x * (ballRadius + 2), y: ball.y + inward.y * (ballRadius + 2) },
        {
          x: ball.x + inward.x * (ballRadius + 2 + anLength),
          y: ball.y + inward.y * (ballRadius + 2 + anLength),
        },
        {
          color: COLORS.pink,
          width: 1.8,
          head: 6,
          alpha: 0.85,
          label: 'aₙ',
          labelOffset: { x: 14, y: 2 },
        },
      );

      // 杆作用力
      const normal = metrics.extras.normalForce;
      if (Math.abs(normal) > 0.02) {
        const sign = normal >= 0 ? 1 : -1;
        const length = Math.min(70, Math.max(12, Math.abs(normal) * 1.8));
        drawArrow(
          ctx,
          { x: ball.x - inward.x * (ballRadius + 2), y: ball.y - inward.y * (ballRadius + 2) },
          {
            x: ball.x - inward.x * (ballRadius + 2) + inward.x * length * sign,
            y: ball.y - inward.y * (ballRadius + 2) + inward.y * length * sign,
          },
          {
            color: normal >= 0 ? COLORS.mint : COLORS.amber,
            width: 1.5,
            head: 6,
            alpha: 0.8,
            label: `N ${normal.toFixed(1)}`,
            labelOffset: { x: -14, y: 12 },
          },
        );
      }
    }

    // 状态
    if (!metrics.extras.canLoop) {
      drawPill(ctx, '能量不足 · 往复摆动', center.x, center.y - radius - 40, {
        align: 'center',
        color: COLORS.amber,
        bg: 'rgba(34,24,8,.8)',
        border: 'rgba(251,191,36,.3)',
        font: '600 11px "Microsoft YaHei", sans-serif',
      });
    }

    // 底部
    const bottomY = height - 30;
    drawPill(ctx, `v ${metrics.extras.speed.toFixed(2)} m/s`, width * 0.5 - 136, bottomY, {
      color: COLORS.text,
      bg: 'rgba(7,18,30,.7)',
      border: 'rgba(148,190,220,.14)',
      font: '600 11px "Cascadia Code", monospace',
    });
    drawPill(ctx, `N ${metrics.extras.normalForce.toFixed(1)} N`, width * 0.5 - 2, bottomY, {
      color: accent,
      bg: hexToRgba(accent, 0.07),
      border: hexToRgba(accent, 0.24),
      font: '600 11px "Cascadia Code", monospace',
    });
    drawPill(ctx, `第 ${metrics.extras.laps} 圈`, width * 0.5 + 112, bottomY, {
      color: COLORS.muted,
      bg: 'rgba(7,18,30,.6)',
      border: 'rgba(148,190,220,.12)',
      font: '600 11px "Cascadia Code", monospace',
    });
  },
};
